import { localized, msg, str } from '@lit/localize';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import '@spectrum-web-components/action-button/sp-action-button.js';
import '@spectrum-web-components/button/sp-button.js';
import '@spectrum-web-components/field-label/sp-field-label.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-edit.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-delete.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-chevron-left.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-play.js';
import '@spectrum-web-components/menu/sp-menu-item.js';
import '@spectrum-web-components/picker/sp-picker.js';
import '@spectrum-web-components/table/sp-table.js';
import '@spectrum-web-components/table/sp-table-body.js';
import '@spectrum-web-components/table/sp-table-cell.js';
import '@spectrum-web-components/table/sp-table-head.js';
import '@spectrum-web-components/table/sp-table-head-cell.js';
import '@spectrum-web-components/table/sp-table-row.js';
import '@spectrum-web-components/textfield/sp-textfield.js';

import '../../components/confirmation-dialog';
import { api } from '../../api';
import type {
  CustomModelDetail,
  CustomModelFileRef,
  CustomModelListItem,
  MyOrganization,
} from '../../api/custom-models';
import { environment } from '../../config/environment';
import { sharedUiStyles } from '../../styles/shared-ui.styles';
import { formatDateTime } from '../../utils/format';
import { FALLBACK_CUSTOM_MODEL_BASE_MODELS } from './chat-model-options';

type View = 'list' | 'create' | 'edit';
type ReasoningLevel = 'fast' | 'balanced' | 'thorough';

// Keep these in sync with src/modules/custom-models/custom-model-generation-settings.ts.
const CUSTOM_MODEL_MAX_TOKENS_MIN = 16;
const CUSTOM_MODEL_MAX_TOKENS_MAX = 16000;

const REASONING_LEVELS: ReasoningLevel[] = ['fast', 'balanced', 'thorough'];

function reasoningLevelLabel(level: ReasoningLevel): string {
  switch (level) {
    case 'fast':
      return msg('Fast');
    case 'balanced':
      return msg('Balanced');
    case 'thorough':
      return msg('Thorough');
  }
}

function reasoningLevelDescription(level: ReasoningLevel): string {
  switch (level) {
    case 'fast':
      return msg('Lowest latency and token use.');
    case 'balanced':
      return msg('Recommended balance of quality and speed.');
    case 'thorough':
      return msg('More reasoning for complex work.');
  }
}

interface ChatModelOption {
  id: string;
  label: string;
  supportsTemperature?: boolean;
}

function activeChatLabel(m: CustomModelListItem): string {
  if (m.activeVersionNumber == null) {
    return m.latestVersion > 0
      ? msg(str`Latest (v${m.latestVersion})`)
      : msg('Latest');
  }
  return `v${m.activeVersionNumber}`;
}

@customElement('kv-module-custom-models')
@localized()
export class KvModuleCustomModels extends LitElement {
  static styles = [
    sharedUiStyles,
    css`
    :host {
      display: block;
      min-height: 100%;
      box-sizing: border-box;
      color: var(--color-app-text, #1f1f1f);
      background:
        linear-gradient(
          180deg,
          var(--color-chat-chat-area-bg, #f8fafc) 0%,
          var(--color-sidebar-bg, #f8fafc) 100%
        );
      --models-surface: var(--kv-surface);
      --models-surface-muted: var(--kv-surface-muted);
      --models-surface-hover: var(--kv-surface-hover);
      --models-border: var(--kv-border);
      --models-accent: var(--kv-accent);
      --models-accent-bg: var(--kv-accent-bg);
      --models-control-bg: var(--kv-control-bg);
      --models-control-hover: var(--kv-control-hover);
      --models-control-active: var(--kv-control-active);
      --models-control-border: var(--kv-control-border);
      --models-shadow: var(--kv-shadow);
      overflow: auto;
      padding: 24px;
    }
    :host([embedded]) {
      height: 100%;
      min-height: 0;
      overflow: hidden;
      padding: 0;
      background: transparent;
    }
    :host-context(sp-theme[color='dark']) {
      --models-shadow: none;
    }
    .models-shell {
      max-width: 1120px;
      min-height: 100%;
      box-sizing: border-box;
    }
    :host([embedded]) .models-shell {
      height: 100%;
      max-width: none;
      overflow: auto;
      padding: 22px 24px 28px;
    }
    .form-page {
      min-width: 0;
    }
    .form-two-col {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 16px;
      align-items: start;
    }
    @media (max-width: 880px) {
      .form-two-col {
        grid-template-columns: 1fr;
      }
    }
    .form-col {
      display: flex;
      flex-direction: column;
      gap: 16px;
      min-width: 0;
    }
    .form-page .section-card {
      margin-bottom: 0;
    }
    .form-footer {
      margin-top: 16px;
      padding: 14px 0 0;
      border: 0;
      border-top: 1px solid var(--models-border);
      border-radius: 0;
      background: transparent;
      box-shadow: none;
    }
    .form-back {
      margin: 0 0 12px;
    }
    h1 {
      margin: 0;
      font-size: 22px;
      font-weight: 750;
      line-height: 1.2;
      letter-spacing: 0;
    }
    h2 {
      margin: 0 0 12px;
      color: var(--color-app-text, #1f1f1f);
      font-size: 14px;
      font-weight: 750;
      line-height: 1.25;
      letter-spacing: 0;
    }
    h3 {
      margin: 0;
      color: var(--color-app-text, #1f1f1f);
      font-size: 13px;
      font-weight: 750;
      line-height: 1.3;
    }
    .page-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 16px;
    }
    .page-header.embedded-header {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr);
      align-items: center;
      justify-content: flex-start;
      padding-bottom: 14px;
      border-bottom: 1px solid var(--models-border);
    }
    .page-title {
      min-width: 0;
    }
    .page-title p {
      margin: 5px 0 0;
      color: var(--color-app-text-secondary, #6b7280);
      font-size: 13px;
      line-height: 1.4;
    }
    .back-button {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      min-height: 32px;
      padding: 0 8px;
      border: 1px solid transparent;
      border-radius: var(--kv-radius-panel);
      background: transparent;
      color: var(--color-app-text, #1f1f1f);
      cursor: pointer;
      font: inherit;
      font-size: 13px;
      font-weight: 650;
      text-decoration: none;
    }
    .back-button:hover {
      background: var(--models-surface-hover);
      border-color: var(--models-border);
    }
    .back-button:focus-visible {
      outline: 2px solid var(--models-accent);
      outline-offset: 2px;
    }
    .back-button sp-icon-chevron-left {
      width: 14px;
      height: 14px;
    }
    .section-card {
      border: 1px solid var(--models-border);
      border-radius: 10px;
      padding: 16px;
      margin-bottom: 16px;
      background: var(--models-surface);
      box-shadow: var(--models-shadow);
      box-sizing: border-box;
    }
    .section-card.subtle {
      background:
        linear-gradient(
          180deg,
          var(--models-surface) 0%,
          var(--models-surface-muted) 100%
        );
    }
    .toolbar {
      display: flex;
      gap: 8px;
      flex-shrink: 0;
    }
    .form-actions {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
    }
    .error {
      margin: 0 0 12px;
      color: var(--color-error-text, #b91c1c);
      font-size: 14px;
      font-weight: 650;
    }
    .muted {
      color: var(--color-app-text-secondary, #6b7280);
      font-size: 13px;
      line-height: 1.45;
      margin: 0 0 10px;
    }
    .field-block {
      margin-bottom: 14px;
    }
    .field-block:last-child {
      margin-bottom: 0;
    }
    sp-field-label {
      color: var(--color-app-text, #1f1f1f);
      font-size: 12px;
      font-weight: 700;
      line-height: 1.3;
      cursor: pointer;
    }
    sp-textfield {
      --mod-textfield-border-color: var(--models-border);
      --mod-textfield-background-color: var(--models-surface);
    }
    sp-picker {
      --mod-picker-background-color-default: var(--models-control-bg);
      --mod-picker-background-color-default-open: var(--models-control-bg);
      --mod-picker-background-color-hover: var(--models-control-hover);
      --mod-picker-background-color-hover-open: var(--models-control-hover);
      --mod-picker-background-color-active: var(--models-control-active);
      --mod-picker-background-color-key-focus: var(--models-control-bg);
      --mod-picker-border-color-default: var(--models-control-border);
      --mod-picker-border-default-open: var(--kv-accent-border);
      --mod-picker-border-color-default-open: var(--kv-accent-border);
      --mod-picker-border-color-hover: var(--kv-accent-border);
      --mod-picker-border-color-hover-open: var(--kv-accent-border);
      --mod-picker-border-active: var(--color-accent-border-strong);
      --mod-picker-border-color-active: var(--color-accent-border-strong);
      --mod-picker-border-color-key-focus: var(--models-accent);
      --mod-picker-focus-indicator-color: var(--models-accent);
      --mod-picker-font-color-default: var(--color-app-text, #1f1f1f);
      --mod-picker-font-color-default-open: var(--color-app-text, #1f1f1f);
      --mod-picker-font-color-hover: var(--color-app-text, #1f1f1f);
      --mod-picker-font-color-hover-open: var(--color-app-text, #1f1f1f);
      --mod-picker-font-color-active: var(--color-app-text, #1f1f1f);
      --mod-picker-font-color-key-focus: var(--color-app-text, #1f1f1f);
      --mod-picker-icon-color-default: var(--models-accent);
      --mod-picker-icon-color-default-open: var(--models-accent);
      --mod-picker-icon-color-hover: var(--models-accent);
      --mod-picker-icon-color-hover-open: var(--models-accent);
      --mod-picker-icon-color-active: var(--models-accent);
      --mod-picker-icon-color-key-focus: var(--models-accent);
    }
    sp-button[variant='secondary'] {
      --mod-button-background-color-default: var(--models-control-bg);
      --mod-button-background-color-hover: var(--models-control-hover);
      --mod-button-background-color-down: var(--models-control-active);
      --mod-button-background-color-focus: var(--models-control-bg);
      --mod-button-border-color-default: var(--models-control-border);
      --mod-button-border-color-hover: var(--kv-accent-border);
      --mod-button-border-color-down: var(--color-accent-border-strong);
      --mod-button-border-color-focus: var(--models-accent);
      --mod-button-content-color-default: var(--models-accent);
      --mod-button-content-color-hover: var(--models-accent);
      --mod-button-content-color-down: var(--models-accent);
      --mod-button-content-color-focus: var(--models-accent);
      --mod-button-focus-ring-color: var(--models-accent);
    }
    .full-width {
      width: 100%;
    }
    .picker-narrow {
      max-width: 280px;
    }
    .picker-medium {
      max-width: 360px;
    }
    .settings-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
    }
    .field-hint {
      margin: 6px 0 0;
      color: var(--color-app-text-secondary, #6b7280);
      font-size: 12px;
      line-height: 1.35;
    }
    .section-card.grow {
      flex: 1;
      min-height: 0;
    }
    .preview-card {
      margin-top: 16px;
    }
    .preview-input {
      width: 100%;
      margin-top: 8px;
    }
    .preview-action {
      margin-top: 10px;
    }
    .preview-meta {
      margin: 0 0 6px;
    }
    .preview-meta.compact {
      margin-bottom: 4px;
    }
    .preview-meta.last {
      margin: 10px 0 0;
    }
    .section-divider {
      margin: 16px 0;
      border: none;
      border-top: 1px solid var(--models-border);
    }
    .version-list {
      max-height: 200px;
      overflow-y: auto;
    }
    .preview-box {
      border: 1px solid var(--models-border);
      border-radius: 8px;
      padding: 14px;
      margin-top: 12px;
      background: var(--models-surface-muted);
      white-space: pre-wrap;
      max-height: 220px;
      overflow-y: auto;
      font-size: 14px;
      line-height: 1.5;
    }
    .file-list {
      list-style: none;
      padding: 0;
      margin: 12px 0 0;
      font-size: 13px;
    }
    .file-list li {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      min-width: 0;
      padding: 8px 0;
      border-bottom: 1px solid var(--models-border);
    }
    .file-list li:last-child {
      border-bottom: none;
    }
    .native-file-input {
      width: 100%;
      max-width: 100%;
      color: var(--color-app-text-secondary, #6b7280);
      font: inherit;
      font-size: 13px;
    }
    .native-file-input::file-selector-button {
      margin-right: 10px;
      padding: 7px 10px;
      border: 1px solid var(--kv-accent-border);
      border-radius: var(--kv-radius-chip);
      background: var(--models-accent-bg);
      color: var(--models-accent);
      cursor: pointer;
      font: inherit;
      font-size: 12px;
      font-weight: 700;
    }
    .models-table-card {
      padding: 0;
      overflow: hidden;
    }
    .table-scroll {
      overflow-x: auto;
    }
    sp-table {
      min-width: 780px;
    }
    .model-name-cell {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
    }
    .model-tile {
      display: grid;
      width: 24px;
      height: 24px;
      flex: 0 0 auto;
      place-items: center;
      color: var(--models-accent);
      font-size: 13px;
      font-weight: 800;
    }
    .model-title {
      display: block;
      color: var(--color-app-text, #1f1f1f);
      font-weight: 750;
      line-height: 1.25;
    }
    .model-description {
      display: block;
      max-width: 340px;
      overflow: hidden;
      color: var(--color-app-text-secondary, #6b7280);
      font-size: 12px;
      line-height: 1.3;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .chip {
      display: inline-flex;
      align-items: center;
      min-height: 22px;
      padding: 2px 8px;
      border: 1px solid var(--kv-accent-border-subtle);
      border-radius: var(--kv-radius-chip);
      background: var(--models-accent-bg);
      color: var(--models-accent);
      font-size: 12px;
      font-weight: 700;
      line-height: 1.2;
      white-space: nowrap;
    }
    .chip.neutral {
      border-color: var(--models-border);
      background: var(--models-surface-muted);
      color: var(--color-app-text-secondary, #6b7280);
    }
    .row-actions {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      justify-content: flex-end;
      width: 100%;
    }
    .empty-panel {
      padding: 28px;
      border: 1px dashed var(--models-border);
      border-radius: var(--kv-radius-panel);
      background: var(--models-surface);
      color: var(--color-app-text-secondary, #6b7280);
      text-align: center;
    }
    .empty-panel strong {
      display: block;
      margin-bottom: 5px;
      color: var(--color-app-text, #1f1f1f);
      font-size: 15px;
    }
    .version-row {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      padding: 8px 0;
      border-bottom: 1px solid var(--models-border);
      font-size: 13px;
    }
    .version-row:last-child {
      border-bottom: none;
    }
    .active-chat-preview {
      margin-top: 16px;
      padding: 14px 16px;
      border-radius: 8px;
      background: var(--models-accent-bg);
      border: 1px solid var(--kv-accent-border-subtle);
    }
    .active-chat-preview h3 {
      margin: 0 0 10px;
      font-size: 14px;
      font-weight: 600;
    }
    .active-chat-preview .prompt-preview {
      margin: 8px 0 0;
      padding: 10px 12px;
      max-height: 220px;
      overflow: auto;
      white-space: pre-wrap;
      font-size: 13px;
      line-height: 1.45;
      background: var(--models-surface);
      border-radius: 6px;
      border: 1px solid var(--models-border);
    }
    .draft-hint {
      font-size: 12px;
      margin: 0 0 10px;
    }
    @media (max-width: 720px) {
      :host {
        padding: 16px;
      }
      :host([embedded]) .models-shell {
        padding: 16px;
      }
      .page-header {
        flex-direction: column;
        align-items: stretch;
      }
      .page-header.embedded-header {
        display: flex;
      }
      .toolbar {
        width: 100%;
      }
      .toolbar sp-button {
        width: 100%;
      }
      .settings-grid {
        grid-template-columns: 1fr;
      }
      h1 {
        font-size: 20px;
      }
    }
    `,
  ];

  @property({ type: Boolean, reflect: true })
  accessor embedded = false;

  @state() accessor view: View = 'list';
  @state() accessor models: CustomModelListItem[] = [];
  @state() accessor chatModels: ChatModelOption[] = [];
  @state() accessor allowedExtensions: string[] = [];
  @state() accessor myOrganizations: MyOrganization[] = [];
  @state() accessor loading = false;
  @state() accessor saving = false;
  @state() accessor previewLoading = false;
  @state() accessor activeVersionSaving = false;
  @state() accessor errorMessage: string | null = null;
  @state() accessor previewText: string | null = null;

  @state() accessor editId: string | null = null;
  @state() accessor editDetail: CustomModelDetail | null = null;
  @state() accessor formName = '';
  @state() accessor formDescription = '';
  @state() accessor formBaseModel = 'gpt-5.5';
  @state() accessor formPrompt = '';
  @state() accessor formReasoningLevel: ReasoningLevel = 'balanced';
  @state() accessor formMaxTokens = '';
  @state() accessor formOrganizationId: string | null = null;
  @state() accessor formVisibility: 'private' | 'org' = 'private';
  @state() accessor formFileRefsToKeep: CustomModelFileRef[] = [];
  @state() accessor formNewFiles: Array<{ name: string; content: string }> = [];
  @state() accessor formPreviewPrompt = '';
  @state() accessor formActiveVersionValue = 'current';
  @state() accessor pendingModelDelete: CustomModelListItem | null = null;

  private _requestId = 0;

  private requestClose() {
    this.dispatchEvent(
      new CustomEvent('custom-models-close', {
        bubbles: true,
        composed: true,
      })
    );
  }

  private notifyChanged() {
    this.dispatchEvent(
      new CustomEvent('custom-models-changed', {
        bubbles: true,
        composed: true,
      })
    );
  }

  connectedCallback(): void {
    super.connectedCallback();
    void this.loadList();
    void this.loadChatModels();
    void this.loadAllowedExtensions();
    void this.loadMyOrganizations();
  }

  private async loadList() {
    const id = ++this._requestId;
    this.loading = true;
    this.errorMessage = null;
    try {
      const list = await api.customModels.list();
      if (id !== this._requestId) return;
      this.models = list;
    } catch (e) {
      if (id !== this._requestId) return;
      this.errorMessage =
        e instanceof Error ? e.message : msg('Failed to load models');
      this.models = [];
    } finally {
      if (id === this._requestId) this.loading = false;
    }
  }

  private async loadChatModels() {
    try {
      const res = await fetch(`${environment.apiBaseUrl}/chat/models`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = (await res.json()) as { models?: ChatModelOption[] };
      this.chatModels = data.models ?? [];
      if (this.chatModels.length && !this.formBaseModel) {
        this.formBaseModel = this.chatModels[0].id;
      }
    } catch {
      this.chatModels = [...FALLBACK_CUSTOM_MODEL_BASE_MODELS];
    }
  }

  private async loadAllowedExtensions() {
    try {
      const { extensions } = await api.customModels.getAllowedExtensions();
      this.allowedExtensions = extensions ?? [];
    } catch {
      this.allowedExtensions = [];
    }
  }

  private async loadMyOrganizations() {
    try {
      this.myOrganizations = await api.customModels.myOrganizations();
    } catch {
      this.myOrganizations = [];
    }
  }

  private onBaseModelChange(e: Event) {
    const t = e.target as { value?: string };
    this.formBaseModel = t?.value ?? this.formBaseModel;
  }

  private openCreate() {
    this.view = 'create';
    this.editId = null;
    this.editDetail = null;
    this.formName = '';
    this.formDescription = '';
    this.formBaseModel = this.chatModels[0]?.id ?? 'gpt-5.5';
    this.formPrompt = '';
    this.formReasoningLevel = 'balanced';
    this.formMaxTokens = '';
    this.formOrganizationId = null;
    this.formVisibility = 'private';
    this.formFileRefsToKeep = [];
    this.formNewFiles = [];
    this.formPreviewPrompt = '';
    this.formActiveVersionValue = 'current';
    this.previewText = null;
    this.errorMessage = null;
  }

  private async openEdit(id: string) {
    this.errorMessage = null;
    try {
      const model = await api.customModels.get(id);
      this.editId = model.id;
      this.editDetail = model;
      this.view = 'edit';
      this.formName = model.name;
      this.formDescription = model.description ?? '';
      this.formBaseModel = model.baseModel;
      this.formPrompt = model.prompt;
      this.formReasoningLevel = model.reasoningLevel ?? 'balanced';
      this.formMaxTokens =
        model.maxTokens == null ? '' : String(model.maxTokens);
      this.formOrganizationId = model.organizationId;
      this.formVisibility = model.organizationId ? 'org' : 'private';
      this.formFileRefsToKeep = model.files ?? [];
      this.formNewFiles = [];
      this.formPreviewPrompt = '';
      this.previewText = null;
      this.formActiveVersionValue =
        model.activeVersionNumber == null
          ? 'current'
          : String(model.activeVersionNumber);
    } catch (e) {
      this.errorMessage =
        e instanceof Error ? e.message : msg('Failed to load model');
    }
  }

  private backToList() {
    this.view = 'list';
    this.editId = null;
    this.editDetail = null;
    void this.loadList();
  }

  private get acceptFileAttribute(): string {
    return (
      this.allowedExtensions
        .map((ext) => `.${ext.replace(/^\./, '')}`)
        .join(',') || '.pdf,.md,.txt,.json,.html'
    );
  }

  private onFilePick = async (e: Event) => {
    const input = e.target as HTMLInputElement;
    const files = input.files;
    if (!files?.length) return;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const buf = await file.arrayBuffer();
      const b64 = btoa(
        new Uint8Array(buf).reduce(
          (acc, byte) => acc + String.fromCharCode(byte),
          ''
        )
      );
      this.formNewFiles = [
        ...this.formNewFiles,
        { name: file.name, content: b64 },
      ];
    }
    input.value = '';
  };

  private removeNewFile(index: number) {
    this.formNewFiles = this.formNewFiles.filter((_, i) => i !== index);
  }

  private onVisibilityChange(e: Event) {
    const v = (e.target as { value?: string }).value;
    if (v === 'org') {
      this.formVisibility = 'org';
      if (!this.formOrganizationId && this.myOrganizations[0]) {
        this.formOrganizationId = this.myOrganizations[0].id;
      }
    } else {
      this.formVisibility = 'private';
      this.formOrganizationId = null;
    }
  }

  private onOrgPickerChange(e: Event) {
    const v = (e.target as { value?: string }).value;
    this.formOrganizationId = v || null;
  }

  private async onActiveVersionPickerChange(e: Event) {
    if (!this.editId) return;
    const raw = (e.target as { value?: string }).value ?? 'current';
    this.formActiveVersionValue = raw;
    const version = raw === 'current' || raw === '' ? null : parseInt(raw, 10);
    if (version !== null && Number.isNaN(version)) return;
    this.activeVersionSaving = true;
    this.errorMessage = null;
    try {
      const detail = await api.customModels.setActiveVersionOnly(
        this.editId,
        version
      );
      this.editDetail = detail;
      this.notifyChanged();
      void this.loadList();
    } catch (err: unknown) {
      const er = err as { body?: { error?: string }; message?: string };
      this.errorMessage =
        er?.body?.error ?? er?.message ?? msg('Failed to set active version');
    } finally {
      this.activeVersionSaving = false;
    }
  }

  private resolvedOrganizationId(): string | null {
    if (this.formVisibility === 'private') return null;
    return this.formOrganizationId;
  }

  private parseOptionalNumber(
    value: string,
    label: string,
    options: { integer?: boolean; min: number; max: number }
  ): { ok: true; value: number | null } | { ok: false } {
    const trimmed = value.trim();
    if (!trimmed) return { ok: true, value: null };
    const parsed = Number(trimmed);
    if (
      !Number.isFinite(parsed) ||
      parsed < options.min ||
      parsed > options.max ||
      (options.integer && !Number.isInteger(parsed))
    ) {
      this.errorMessage = options.integer
        ? msg(
            str`${label} must be an integer between ${options.min} and ${options.max}`
          )
        : msg(str`${label} must be between ${options.min} and ${options.max}`);
      return { ok: false };
    }
    return { ok: true, value: parsed };
  }

  private parseGenerationSettings():
    | {
        ok: true;
        reasoningLevel: ReasoningLevel;
        maxTokens: number | null;
      }
    | { ok: false } {
    const maxTokens = this.parseOptionalNumber(
      this.formMaxTokens,
      msg('Max tokens'),
      {
        integer: true,
        min: CUSTOM_MODEL_MAX_TOKENS_MIN,
        max: CUSTOM_MODEL_MAX_TOKENS_MAX,
      }
    );
    if (!maxTokens.ok) return { ok: false };
    return {
      ok: true,
      reasoningLevel: this.formReasoningLevel,
      maxTokens: maxTokens.value,
    };
  }

  private async saveCreate() {
    if (!this.formName?.trim()) {
      this.errorMessage = msg('Name is required');
      return;
    }
    if (!this.formPrompt?.trim()) {
      this.errorMessage = msg('Prompt is required');
      return;
    }
    if (this.formVisibility === 'org' && !this.formOrganizationId) {
      this.errorMessage = msg('Select an organization or choose Private');
      return;
    }
    const settings = this.parseGenerationSettings();
    if (!settings.ok) return;
    this.saving = true;
    this.errorMessage = null;
    try {
      await api.customModels.create({
        name: this.formName.trim(),
        description: this.formDescription?.trim() || null,
        baseModel: this.formBaseModel,
        prompt: this.formPrompt.trim(),
        reasoningLevel: settings.reasoningLevel,
        maxTokens: settings.maxTokens,
        organizationId: this.resolvedOrganizationId(),
        fileRefsToKeep: this.formFileRefsToKeep,
        newFiles: this.formNewFiles.length ? this.formNewFiles : undefined,
      });
      this.notifyChanged();
      this.backToList();
    } catch (e: unknown) {
      const err = e as { body?: { error?: string }; message?: string };
      this.errorMessage =
        err?.body?.error ?? err?.message ?? msg('Failed to create');
    } finally {
      this.saving = false;
    }
  }

  private async saveUpdate() {
    if (!this.editId) return;
    if (!this.formName?.trim()) {
      this.errorMessage = msg('Name is required');
      return;
    }
    if (!this.formPrompt?.trim()) {
      this.errorMessage = msg('Prompt is required');
      return;
    }
    if (this.formVisibility === 'org' && !this.formOrganizationId) {
      this.errorMessage = msg('Select an organization or choose Private');
      return;
    }
    const settings = this.parseGenerationSettings();
    if (!settings.ok) return;
    this.saving = true;
    this.errorMessage = null;
    try {
      const detail = await api.customModels.update(this.editId, {
        name: this.formName.trim(),
        description: this.formDescription?.trim() || null,
        baseModel: this.formBaseModel,
        prompt: this.formPrompt.trim(),
        reasoningLevel: settings.reasoningLevel,
        maxTokens: settings.maxTokens,
        organizationId: this.resolvedOrganizationId(),
        fileRefsToKeep: this.formFileRefsToKeep,
        newFiles: this.formNewFiles.length ? this.formNewFiles : undefined,
      });
      this.editDetail = detail;
      this.formActiveVersionValue =
        detail.activeVersionNumber == null
          ? 'current'
          : String(detail.activeVersionNumber);
      this.notifyChanged();
      this.backToList();
    } catch (e: unknown) {
      const err = e as { body?: { error?: string }; message?: string };
      this.errorMessage =
        err?.body?.error ?? err?.message ?? msg('Failed to update');
    } finally {
      this.saving = false;
    }
  }

  private requestDeleteModel(model: CustomModelListItem) {
    this.pendingModelDelete = model;
  }

  private cancelDeleteModel = () => {
    this.pendingModelDelete = null;
  };

  private confirmDeleteModel = async () => {
    const model = this.pendingModelDelete;
    if (!model) return;
    this.pendingModelDelete = null;
    try {
      await api.customModels.delete(model.id);
      this.notifyChanged();
      void this.loadList();
    } catch (e: unknown) {
      const err = e as { body?: { error?: string }; message?: string };
      this.errorMessage =
        err?.body?.error ?? err?.message ?? msg('Failed to delete');
    }
  };

  private async runPreview() {
    const prompt = this.formPreviewPrompt.trim() || 'Hello';
    const settings = this.parseGenerationSettings();
    if (!settings.ok) return;
    this.previewLoading = true;
    this.previewText = null;
    this.errorMessage = null;
    try {
      const fileIds = this.formFileRefsToKeep.map((f) => f.id);
      const result = await api.customModels.preview({
        prompt,
        baseModel: this.formBaseModel,
        instructions: this.formPrompt.trim() || ' ',
        reasoningLevel: settings.reasoningLevel,
        maxTokens: settings.maxTokens,
        fileIds: fileIds.length ? fileIds : undefined,
      });
      this.previewText = result.text;
    } catch (e: unknown) {
      const err = e as { body?: { error?: string }; message?: string };
      this.errorMessage =
        err?.body?.error ?? err?.message ?? msg('Preview failed');
    } finally {
      this.previewLoading = false;
    }
  }

  private renderList() {
    return html`
      <div>
        <div class="page-header">
          <div class="page-title">
            <h1>${this.embedded ? msg('Your models') : msg('Custom models')}</h1>
            <p>
              ${msg('Reusable chat instructions, knowledge files, and revisions for assistant conversations.')}
            </p>
          </div>
          <div class="toolbar">
            <sp-button variant="accent" @click=${this.openCreate}>
              ${msg('Create model')}
            </sp-button>
          </div>
        </div>
        ${this.loading ? html`<p class="muted">${msg('Loading...')}</p>` : null}
        ${this.errorMessage ? html`<p class="error">${this.errorMessage}</p>` : null}
        ${
          !this.loading && this.models.length === 0
            ? html`<div class="empty-panel">
                <strong>${msg('No custom models yet')}</strong>
                <span>${msg('Create one to make a tailored assistant available in Chat.')}</span>
              </div>`
            : null
        }
        ${
          !this.loading && this.models.length > 0
            ? html`
              <div class="section-card models-table-card">
                <div class="table-scroll">
                  <sp-table>
                    <sp-table-head>
                      <sp-table-head-cell>${msg('Name')}</sp-table-head-cell>
                      <sp-table-head-cell>${msg('Base model')}</sp-table-head-cell>
                      <sp-table-head-cell>${msg('Active in chat')}</sp-table-head-cell>
                      <sp-table-head-cell>${msg('Visibility')}</sp-table-head-cell>
                      <sp-table-head-cell></sp-table-head-cell>
                    </sp-table-head>
                    <sp-table-body>
                      ${this.models.map(
                        (m) => html`
                          <sp-table-row>
                            <sp-table-cell>
                              <span class="model-name-cell">
                                <span class="model-tile">
                                  ${(m.name || 'M').trim().charAt(0).toUpperCase()}
                                </span>
                                <span>
                                  <span class="model-title">${m.name}</span>
                                  <span class="model-description">
                                    ${m.description || msg('No description')}
                                  </span>
                                </span>
                              </span>
                            </sp-table-cell>
                            <sp-table-cell>${m.baseModel}</sp-table-cell>
                            <sp-table-cell>
                              <span class="chip">${activeChatLabel(m)}</span>
                            </sp-table-cell>
                            <sp-table-cell>
                              <span class="chip neutral">
                                ${m.organizationId ? msg('Organization') : msg('Private')}
                              </span>
                            </sp-table-cell>
                            <sp-table-cell>
                              <span class="row-actions">
                                <sp-action-button
                                  label=${msg('Edit')}
                                  @click=${() => this.openEdit(m.id)}
                                >
                                  <sp-icon-edit slot="icon"></sp-icon-edit>
                                </sp-action-button>
                                <sp-action-button
                                  label=${msg('Delete')}
                                  quiet
                                  @click=${() => this.requestDeleteModel(m)}
                                >
                                  <sp-icon-delete slot="icon"></sp-icon-delete>
                                </sp-action-button>
                              </span>
                            </sp-table-cell>
                          </sp-table-row>
                        `
                      )}
                    </sp-table-body>
                  </sp-table>
                </div>
              </div>
            `
            : null
        }
      </div>
    `;
  }

  private renderVisibilityBlock() {
    const orgDisabled = this.myOrganizations.length === 0;
    return html`
      <div class="section-card">
        <h2>${msg('Visibility')}</h2>
        <p class="muted">
          ${msg('Private: only you. Organization: everyone in the selected org can use this model in chat.')}
        </p>
        <div class="field-block">
          <sp-field-label>${msg('Who can use this model')}</sp-field-label>
          <sp-picker
            .value=${this.formVisibility}
            @change=${this.onVisibilityChange}
            label=${msg('Visibility')}
            class="picker-narrow"
          >
            <sp-menu-item value="private">${msg('Private')}</sp-menu-item>
            <sp-menu-item value="org" ?disabled=${orgDisabled}>
              ${msg('Organization')}
              ${orgDisabled ? msg(' (no memberships)') : ''}
            </sp-menu-item>
          </sp-picker>
        </div>
        ${
          this.formVisibility === 'org'
            ? html`
              <div class="field-block">
                <sp-field-label>${msg('Organization')}</sp-field-label>
                <sp-picker
                  .value=${this.formOrganizationId ?? ''}
                  @change=${this.onOrgPickerChange}
                  label=${msg('Organization')}
                  class="picker-medium"
                >
                  ${this.myOrganizations.map(
                    (o) =>
                      html`<sp-menu-item value=${o.id}>${o.name}</sp-menu-item>`
                  )}
                </sp-picker>
              </div>
            `
            : null
        }
      </div>
    `;
  }

  private renderVersionsBlock() {
    const d = this.editDetail;
    if (!d?.versions?.length) return null;
    const sorted = [...d.versions].sort((a, b) => b.version - a.version);
    return html`
      <div class="section-card">
        <h2>${msg('Revisions')}</h2>
        <p class="muted">
          ${msg('Each save creates a revision. Choose which revision is used when this model is selected in chat.')}
        </p>
        <div class="field-block">
          <sp-field-label>${msg('Active for chat')}</sp-field-label>
          <sp-picker
            .value=${this.formActiveVersionValue}
            @change=${this.onActiveVersionPickerChange}
            label=${msg('Active revision')}
            class="picker-medium"
            ?disabled=${this.activeVersionSaving}
          >
            <sp-menu-item value="current">
              ${msg(str`Latest saved (v${d.latestVersion})`)}
            </sp-menu-item>
            ${sorted.map(
              (v) => html`
                <sp-menu-item value=${String(v.version)}>
                  v${v.version} · ${formatDateTime(v.createdAt)}
                </sp-menu-item>
              `
            )}
          </sp-picker>
          ${
            this.activeVersionSaving
              ? html`<p class="muted">${msg('Updating...')}</p>`
              : null
          }
        </div>
        ${
          d.activeChatPreview
            ? html`
              <div class="active-chat-preview">
                <h3>${msg('What chat uses now')} - ${d.activeChatPreview.revisionLabel}</h3>
                <p class="muted preview-meta">
                  <strong>${msg('Base model')}:</strong> ${d.activeChatPreview.baseModel}
                </p>
                <p class="muted preview-meta">
                  <strong>${msg('Reasoning')}:</strong>
                  ${reasoningLevelLabel(d.activeChatPreview.reasoningLevel)}
                </p>
                <p class="muted preview-meta">
                  <strong>${msg('Max tokens')}:</strong>
                  ${
                    d.activeChatPreview.maxTokens == null
                      ? msg('Default')
                      : d.activeChatPreview.maxTokens
                  }
                </p>
                <p class="muted preview-meta compact">${msg('Instructions')}:</p>
                <div class="prompt-preview">${d.activeChatPreview.prompt}</div>
                <p class="muted preview-meta last">
                  <strong>${msg('Files')}:</strong>
                  ${
                    (d.activeChatPreview.files?.length ?? 0) === 0
                      ? ` ${msg('None')}`
                      : d.activeChatPreview.files
                          ?.map((f) => f.name ?? f.id)
                          .join(', ')
                  }
                </p>
              </div>
            `
            : null
        }
        <hr class="section-divider" />
        <div class="version-list">
          ${sorted.map(
            (v) => html`
              <div class="version-row">
                <span><strong>v${v.version}</strong></span>
                <span class="muted">${formatDateTime(v.createdAt)}</span>
              </div>
            `
          )}
        </div>
      </div>
    `;
  }

  private renderForm(title: string, onSave: () => void, isEdit: boolean) {
    const fileRefs = this.formFileRefsToKeep;
    const newFiles = this.formNewFiles;
    const allFileNames = [
      ...fileRefs.map((f) => f.name ?? f.id),
      ...newFiles.map((f) => f.name),
    ];
    return html`
      <div class="form-page">
        <div class="form-back">
          <sp-action-button
            quiet
            label=${msg('Back to custom models list')}
            @click=${this.backToList}
          >
            <sp-icon-chevron-left slot="icon"></sp-icon-chevron-left>
            ${msg('Back to list')}
          </sp-action-button>
        </div>
        <h1>${title}</h1>

        <div class="form-two-col">
          <div class="form-col">
            <div class="section-card">
              <h2>${msg('Basic information')}</h2>
              <div class="field-block">
                <sp-field-label for="name">${msg('Name')}</sp-field-label>
                <sp-textfield
                  id="name"
                  value=${this.formName}
                  @input=${(e: Event) => {
                    this.formName = (e.target as HTMLInputElement).value;
                  }}
                  placeholder=${msg('My model')}
                  class="full-width"
                ></sp-textfield>
              </div>
              <div class="field-block">
                <sp-field-label for="desc">${msg('Description (optional)')}</sp-field-label>
                <sp-textfield
                  id="desc"
                  value=${this.formDescription}
                  @input=${(e: Event) => {
                    this.formDescription = (e.target as HTMLInputElement).value;
                  }}
                  placeholder=${msg('Short description')}
                  class="full-width"
                ></sp-textfield>
              </div>
              <div class="field-block">
                <sp-field-label for="base">${msg('Base model')}</sp-field-label>
                <sp-picker
                  id="base"
                  .value=${this.formBaseModel}
                  @change=${this.onBaseModelChange}
                  label=${msg('Base model')}
                  class="full-width"
                >
                  ${this.chatModels.map(
                    (m) =>
                      html`<sp-menu-item value=${m.id}>${m.label}</sp-menu-item>`
                  )}
                </sp-picker>
              </div>
              <div class="settings-grid">
                <div class="field-block">
                  <sp-field-label for="reasoningLevel">
                    ${msg('Reasoning level')}
                  </sp-field-label>
                  <sp-picker
                    id="reasoningLevel"
                    .value=${this.formReasoningLevel}
                    @change=${(e: Event) => {
                      this.formReasoningLevel = (
                        e.target as unknown as { value: ReasoningLevel }
                      ).value;
                    }}
                    label=${msg('Reasoning level')}
                    class="full-width"
                  >
                    ${REASONING_LEVELS.map(
                      (level) => html`
                        <sp-menu-item value=${level}>
                          ${reasoningLevelLabel(level)}
                        </sp-menu-item>
                      `
                    )}
                  </sp-picker>
                  <p class="field-hint">
                    ${reasoningLevelDescription(this.formReasoningLevel)}
                  </p>
                </div>
                <div class="field-block">
                  <sp-field-label for="maxTokens">
                    ${msg('Max tokens')}
                  </sp-field-label>
                  <sp-textfield
                    id="maxTokens"
                    type="number"
                    min=${String(CUSTOM_MODEL_MAX_TOKENS_MIN)}
                    max=${String(CUSTOM_MODEL_MAX_TOKENS_MAX)}
                    step="1"
                    inputmode="numeric"
                    value=${this.formMaxTokens}
                    @input=${(e: Event) => {
                      this.formMaxTokens = (e.target as HTMLInputElement).value;
                    }}
                    placeholder=${msg('Default')}
                    class="full-width"
                  ></sp-textfield>
                </div>
              </div>
            </div>

            <div class="section-card grow">
              <h2>${msg('Instructions')}</h2>
              <p class="muted draft-hint">
                <strong>${msg('Latest saved draft')}</strong> - ${msg('what the next save stores. Chat may use a different revision; pick it under Revisions and check')} <strong>${msg('What chat uses')}</strong> ${msg('below.')}
              </p>
              <sp-textfield
                id="prompt"
                multiline
                rows=${18}
                value=${this.formPrompt}
                @input=${(e: Event) => {
                  this.formPrompt = (e.target as HTMLInputElement).value;
                }}
                placeholder=${msg('You are a helpful assistant...')}
                class="full-width"
              ></sp-textfield>
            </div>
          </div>

          <div class="form-col">
            <div class="section-card">
              <h2>${msg('Knowledge files')}</h2>
              <p class="muted">
                ${msg('PDF, Markdown, text, JSON, code, etc. Images are not supported.')}
              </p>
              <input
                type="file"
                class="native-file-input"
                accept=${this.acceptFileAttribute}
                multiple
                @change=${this.onFilePick}
              />
              ${
                allFileNames.length
                  ? html`
                    <ul class="file-list">
                      ${fileRefs.map(
                        (f) =>
                          html`<li>${f.name ?? f.id} <span class="muted">${msg('(saved)')}</span></li>`
                      )}
                      ${newFiles.map(
                        (f, i) => html`
                          <li>
                            ${f.name}
                            <sp-action-button
                              quiet
                              label=${msg('Remove')}
                              @click=${() => this.removeNewFile(i)}
                            ></sp-action-button>
                          </li>
                        `
                      )}
                    </ul>
                  `
                  : null
              }
            </div>

            ${this.renderVisibilityBlock()}
            ${isEdit ? this.renderVersionsBlock() : null}
          </div>
        </div>

        <div class="form-footer">
          ${this.errorMessage ? html`<p class="error">${this.errorMessage}</p>` : null}
          <div class="form-actions">
            <sp-button variant="accent" ?disabled=${this.saving} @click=${onSave}>
              ${this.saving ? msg('Saving...') : msg('Save')}
            </sp-button>
            <sp-button variant="secondary" @click=${this.backToList}>${msg('Cancel')}</sp-button>
          </div>
        </div>

        <div class="section-card preview-card">
          <h2>${msg('Preview')}</h2>
          <p class="muted">${msg('Try the current instructions and listed file IDs (saved files only).')}</p>
          <sp-textfield
            placeholder=${msg('Type a test message...')}
            value=${this.formPreviewPrompt}
            @input=${(e: Event) => {
              this.formPreviewPrompt = (e.target as HTMLInputElement).value;
            }}
            class="preview-input"
          ></sp-textfield>
          <sp-button
            variant="secondary"
            class="preview-action"
            ?disabled=${this.previewLoading}
            @click=${this.runPreview}
          >
            ${this.previewLoading ? msg('Loading...') : msg('Run preview')}
          </sp-button>
          ${
            this.previewText !== null
              ? html`<div class="preview-box">${this.previewText}</div>`
              : null
          }
        </div>
      </div>
    `;
  }

  private renderEmbeddedHeader() {
    if (!this.embedded) return null;
    return html`
      <div class="page-header embedded-header">
        <a
          href="/?module=chat"
          class="back-button"
          aria-label=${msg('Back to chat')}
          @click=${() => this.requestClose()}
        >
          <sp-icon-chevron-left></sp-icon-chevron-left>
          ${msg('Back to chat')}
        </a>
        <div class="page-title">
          <h1>${msg('Custom models')}</h1>
          <p>
            ${msg('Build reusable chat instructions, knowledge files, and model revisions without leaving Chat.')}
          </p>
        </div>
      </div>
    `;
  }

  render() {
    const content =
      this.view === 'list'
        ? this.renderList()
        : this.view === 'create'
          ? this.renderForm(
              msg('New custom model'),
              () => this.saveCreate(),
              false
            )
          : this.renderForm(
              msg('Edit custom model'),
              () => this.saveUpdate(),
              true
            );
    return html`
      <div class="models-shell">
        ${this.renderEmbeddedHeader()}
        ${content}
      </div>
      ${
        this.pendingModelDelete
          ? html`
            <kv-confirmation-dialog
              heading=${msg('Delete custom model')}
              confirm-label=${msg('Delete')}
              @cancel=${this.cancelDeleteModel}
              @confirm=${this.confirmDeleteModel}
            >
              <p>
                ${msg(
                  str`Delete custom model "${this.pendingModelDelete.name}"? This removes the model configuration and its revisions from the custom models list.`
                )}
              </p>
            </kv-confirmation-dialog>
          `
          : nothing
      }
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-module-custom-models': KvModuleCustomModels;
  }
}
